import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Eye, Database, Mail, Lock, Users, AlertCircle } from 'lucide-react';
import './Terms.css';

export default function Privacy() {
  return (
    <div className="legal-page">
      <div className="legal-container">
        {/* Header */}
        <div className="legal-header">
          <Link to="/login" className="back-button">
            <ArrowLeft size={20} />
            Volver
          </Link>
          <div className="legal-title-section">
            <div className="legal-icon">
              <Shield size={32} />
            </div>
            <div>
              <h1 className="legal-title">Política de Privacidad</h1>
              <p className="legal-subtitle">Última actualización: {new Date().toLocaleDateString('es-ES')}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="legal-content">
          <section className="legal-section">
            <h2 className="section-title">
              <Eye size={24} />
              1. Información que Recopilamos
            </h2>
            <p className="section-text">
              Recopilamos información que usted nos proporciona directamente y datos que obtenemos automáticamente 
              cuando utiliza nuestros servicios.
            </p>
            
            <h3 className="subsection-title">1.1 Información Personal</h3>
            <ul className="legal-list">
              <li><strong>Datos de cuenta:</strong> Nombre de usuario, dirección de correo electrónico</li>
              <li><strong>Información de perfil:</strong> Preferencias de monitoreo y configuraciones</li>
              <li><strong>Datos de pago:</strong> Información procesada por Stripe (no almacenamos datos de tarjetas)</li>
            </ul>

            <h3 className="subsection-title">1.2 Información de Uso</h3>
            <ul className="legal-list">
              <li><strong>Datos de monitoreo:</strong> Partidos seleccionados, umbrales de precio, frecuencia de alertas</li>
              <li><strong>Datos técnicos:</strong> Dirección IP, tipo de navegador, sistema operativo</li>
              <li><strong>Datos de interacción:</strong> Páginas visitadas, tiempo de sesión, acciones realizadas</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2 className="section-title">
              <Database size={24} />
              2. Cómo Utilizamos su Información
            </h2>
            <p className="section-text">
              Utilizamos su información personal únicamente para los siguientes propósitos:
            </p>
            
            <h3 className="subsection-title">2.1 Prestación del Servicio</h3>
            <ul className="legal-list">
              <li>Procesar y enviar alertas de precios según sus configuraciones</li>
              <li>Mantener su cuenta y preferencias de usuario</li>
              <li>Proporcionar soporte técnico y atención al cliente</li>
              <li>Procesar pagos y gestionar suscripciones</li>
            </ul>

            <h3 className="subsection-title">2.2 Mejora del Servicio</h3>
            <ul className="legal-list">
              <li>Analizar patrones de uso para mejorar la funcionalidad</li>
              <li>Desarrollar nuevas características y servicios</li>
              <li>Optimizar el rendimiento de la plataforma</li>
            </ul>

            <h3 className="subsection-title">2.3 Comunicación</h3>
            <ul className="legal-list">
              <li>Enviar alertas de precios según sus configuraciones</li>
              <li>Notificar sobre actualizaciones del servicio</li>
              <li>Proporcionar soporte técnico cuando sea necesario</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2 className="section-title">
              <Lock size={24} />
              3. Compartir Información
            </h2>
            <p className="section-text">
              <strong>No vendemos, alquilamos ni compartimos su información personal con terceros</strong>, 
              excepto en las siguientes circunstancias limitadas:
            </p>
            
            <h3 className="subsection-title">3.1 Proveedores de Servicios</h3>
            <ul className="legal-list">
              <li><strong>Stripe:</strong> Para procesamiento de pagos (sujeto a su política de privacidad)</li>
              <li><strong>Firebase:</strong> Para autenticación y almacenamiento de datos (sujeto a políticas de Google)</li>
              <li><strong>Proveedores de email:</strong> Para envío de alertas (Postmark, Amazon SES)</li>
            </ul>

            <h3 className="subsection-title">3.2 Requisitos Legales</h3>
            <p className="section-text">
              Podemos divulgar información si es requerido por ley, orden judicial, o para proteger 
              nuestros derechos legales y la seguridad de nuestros usuarios.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="section-title">
              <Shield size={24} />
              4. Seguridad de Datos
            </h2>
            <p className="section-text">
              Implementamos medidas de seguridad técnicas y organizativas para proteger su información:
            </p>
            
            <h3 className="subsection-title">4.1 Medidas Técnicas</h3>
            <ul className="legal-list">
              <li><strong>Cifrado:</strong> Todos los datos se transmiten y almacenan cifrados</li>
              <li><strong>Autenticación:</strong> Sistemas de autenticación seguros y verificación de dos factores</li>
              <li><strong>Monitoreo:</strong> Supervisión continua de accesos y actividades</li>
              <li><strong>Copias de seguridad:</strong> Respaldo regular y seguro de todos los datos</li>
            </ul>

            <h3 className="subsection-title">4.2 Medidas Organizativas</h3>
            <ul className="legal-list">
              <li>Acceso limitado a datos personales solo al personal autorizado</li>
              <li>Capacitación regular del equipo en seguridad de datos</li>
              <li>Políticas internas estrictas de manejo de información</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2 className="section-title">
              <Users size={24} />
              5. Sus Derechos
            </h2>
            <p className="section-text">
              Usted tiene los siguientes derechos respecto a su información personal:
            </p>
            
            <h3 className="subsection-title">5.1 Derechos de Acceso y Control</h3>
            <ul className="legal-list">
              <li><strong>Acceso:</strong> Solicitar una copia de sus datos personales</li>
              <li><strong>Rectificación:</strong> Corregir información inexacta o incompleta</li>
              <li><strong>Eliminación:</strong> Solicitar la eliminación de sus datos personales</li>
              <li><strong>Portabilidad:</strong> Obtener sus datos en un formato estructurado</li>
            </ul>

            <h3 className="subsection-title">5.2 Derechos de Comunicación</h3>
            <ul className="legal-list">
              <li><strong>Opt-out:</strong> Cancelar suscripciones a comunicaciones no esenciales</li>
              <li><strong>Preferencias:</strong> Modificar configuraciones de privacidad en su cuenta</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2 className="section-title">
              <AlertCircle size={24} />
              6. Cookies y Tecnologías Similares
            </h2>
            <p className="section-text">
              Utilizamos cookies y tecnologías similares para mejorar su experiencia:
            </p>
            
            <h3 className="subsection-title">6.1 Tipos de Cookies</h3>
            <ul className="legal-list">
              <li><strong>Cookies esenciales:</strong> Necesarias para el funcionamiento básico del sitio</li>
              <li><strong>Cookies de rendimiento:</strong> Para analizar el uso y mejorar el servicio</li>
              <li><strong>Cookies de funcionalidad:</strong> Para recordar sus preferencias</li>
            </ul>

            <h3 className="subsection-title">6.2 Gestión de Cookies</h3>
            <p className="section-text">
              Puede controlar las cookies a través de la configuración de su navegador. 
              Sin embargo, deshabilitar ciertas cookies puede afectar la funcionalidad del servicio.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="section-title">
              <Mail size={24} />
              7. Retención de Datos
            </h2>
            <p className="section-text">
              Conservamos su información personal durante el tiempo necesario para:
            </p>
            <ul className="legal-list">
              <li>Proporcionar nuestros servicios mientras su cuenta esté activa</li>
              <li>Cumplir con obligaciones legales y regulatorias</li>
              <li>Resolver disputas y hacer cumplir nuestros acuerdos</li>
            </ul>
            <p className="section-text">
              <strong>Período de retención:</strong> Mantenemos sus datos durante 3 años después de la cancelación 
              de su cuenta, a menos que solicite su eliminación antes.
            </p>
          </section>

          <section className="legal-section">
            <h2 className="section-title">
              <Shield size={24} />
              8. Cambios a esta Política
            </h2>
            <p className="section-text">
              Podemos actualizar esta Política de Privacidad ocasionalmente. Los cambios significativos 
              serán notificados a través de:
            </p>
            <ul className="legal-list">
              <li>Notificación en la plataforma</li>
              <li>Correo electrónico a su dirección registrada</li>
              <li>Actualización de la fecha de "última modificación"</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2 className="section-title">
              <Mail size={24} />
              9. Contacto
            </h2>
            <p className="section-text">
              Para ejercer sus derechos o si tiene preguntas sobre esta Política de Privacidad:
            </p>
            <div className="contact-info">
              <p><strong>Email:</strong> blueecodelabs@gmail.com</p>
            </div>
          </section>
        </div>

        {/* Footer Actions */}
        <div className="legal-footer">
          <Link to="/terms" className="legal-link">
            <Shield size={16} />
            Términos y Condiciones
          </Link>
          <Link to="/support" className="legal-link">
            <Mail size={16} />
            Soporte
          </Link>
        </div>
      </div>
    </div>
  );
}
